import { useMemo, useState, useEffect } from 'react';
import { SnackbarProvider } from 'notistack';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import Landing from './Pages/Landing';
import PricingPage from './Pages/Pricing';
import Signup from './Pages/Signup/index';
import SignupComplete from './Pages/SignupComplete';
import Login from './Pages/Login';
import ForgotPassword from './Pages/ForgotPassword';
import ResetPassword from './Pages/ResetPassword';
import BidAccepted from './Pages/BidAccepted';
import BidStatus from './Pages/BidStatus';
import NotFound from './Pages/NotFound';
import Terms from './Pages/Legal/Terms';
import Privacy from './Pages/Legal/Privacy';
import SmsOptIn from './Pages/Legal/SmsOptIn';
import JobListPage from './Pages/Jobs/JobListPage';
import JobDetailPage from './Pages/Jobs/JobDetailPage';
import SchedulePage from './Pages/Schedule';
import TimePage from './Pages/Time';
import AnalyticsPage from './Pages/Analytics';
import { AppProvider, useAppContext } from './Context/AppContext';
import { RealtimeProvider } from './Context/RealtimeContext';
import { JobDetailProvider } from './Pages/Jobs/context/JobDetailContext';
import Layout from './Components/Layout';
import PageTransitionLoader from './Components/PageTransitionLoader';
import ProtectedRoute from './Components/ProtectedRoute';
import Customers from './Pages/Customers/index';
import Users from './Pages/Users/index';
import UsersRoute from './Components/UsersRoute';
import Organizations from './Pages/Organizations/index';
import Settings from './Pages/Settings/index';
import PaymentSuccess from './Pages/PaymentSuccess';
import PaymentCancelled from './Pages/PaymentCancelled';
import SuperuserRoute from './Components/SuperuserRoute';
import ManagerRoute from './Components/ManagerRoute';
import { APP_NAME } from './Shared/branding';

const getTheme = (mode) =>
	createTheme({
		palette: {
			mode,
			primary:
				mode === 'light'
					? {
							main: '#4A7A9C',
							light: '#7BA3C4',
							dark: '#356380',
							contrastText: '#ffffff',
						}
					: {
							main: '#7BA3C4',
							light: '#A8C5DC',
							dark: '#5A8AAD',
							contrastText: '#0A0A0F',
						},
			secondary: {
				main: mode === 'light' ? '#F5F5F7' : '#2C2C2E',
				light: mode === 'light' ? '#FFFFFF' : '#3A3A3C',
				dark: mode === 'light' ? '#E5E5EA' : '#1C1C1E',
				contrastText: mode === 'light' ? '#1C1C1E' : '#F5F5F7',
			},
			error: {
				main: mode === 'light' ? '#EF4444' : '#F87171',
				light: '#FCA5A5',
				dark: '#DC2626',
			},
			warning: {
				main: mode === 'light' ? '#F59E0B' : '#FBBF24',
				light: '#FCD34D',
				dark: '#D97706',
			},
			success: {
				main: mode === 'light' ? '#10B981' : '#6EE7B7',
				light: '#6EE7B7',
				dark: '#059669',
			},
			info: {
				main: mode === 'light' ? '#06B6D4' : '#67E8F9',
				light: '#67E8F9',
				dark: '#0891B2',
			},
			background:
				mode === 'light'
					? {
							default: '#F2F2F7',
							paper: '#FFFFFF',
						}
					: {
							default: '#000000',
							paper: '#1C1C1E',
						},
			text:
				mode === 'light'
					? {
							primary: '#1C1C1E',
							secondary: '#8E8E93',
						}
					: {
							primary: '#F5F5F7',
							secondary: '#AEAEB2',
						},
			divider:
				mode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.08)',
		},
		shape: {
			borderRadius: 16,
		},
		typography: {
			fontFamily:
				'"Inter", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
			h5: {
				fontWeight: 700,
				letterSpacing: '-0.03em',
			},
			h6: {
				fontWeight: 600,
				letterSpacing: '-0.02em',
			},
		},
		transitions: {
			duration: {
				enteringScreen: 350,
				leavingScreen: 280,
			},
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						scrollbarWidth: 'thin',
						scrollbarColor:
							mode === 'dark' ? '#3A3A3C #000000' : '#C7C7CC #F2F2F7',
						'&::-webkit-scrollbar, & *::-webkit-scrollbar': {
							width: 6,
							height: 6,
						},
						'&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
							borderRadius: 6,
							backgroundColor: mode === 'dark' ? '#3A3A3C' : '#C7C7CC',
						},
						'&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
							backgroundColor: 'transparent',
						},
					},
				},
			},
			MuiCard: {
				styleOverrides: {
					root: {
						backgroundImage: 'none',
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundImage: 'none',
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						borderRadius: 12,
						...(mode === 'dark'
							? {
									'& .MuiOutlinedInput-notchedOutline': {
										borderColor: 'rgba(255, 255, 255, 0.12)',
									},
									'&:hover .MuiOutlinedInput-notchedOutline': {
										borderColor: 'rgba(255, 255, 255, 0.2)',
									},
								}
							: {
									'& .MuiOutlinedInput-notchedOutline': {
										borderColor: 'rgba(0, 0, 0, 0.08)',
									},
								}),
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						textTransform: 'none',
						fontWeight: 600,
						borderRadius: 12,
						transition: 'transform 0.15s ease, box-shadow 0.2s ease',
						'&:active': {
							transform: 'scale(0.97)',
						},
					},
					contained: {
						boxShadow: 'none',
						'&:hover': {
							boxShadow:
								mode === 'light'
									? '0 4px 14px rgba(74, 122, 156, 0.28)'
									: '0 4px 14px rgba(123, 163, 196, 0.22)',
						},
					},
				},
			},
			MuiChip: {
				styleOverrides: {
					root: {
						fontWeight: 500,
					},
					outlined:
						mode === 'dark'
							? {
									borderColor: 'rgba(255, 255, 255, 0.15)',
								}
							: {},
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						borderRadius: 8,
						fontSize: '0.75rem',
						fontWeight: 500,
						backdropFilter: 'blur(12px)',
					},
				},
			},
		},
	});

function AppContent({ toggleDarkMode, darkMode }) {
	const { state } = useAppContext();
	const location = useLocation();

	useEffect(() => {
		if (location.pathname === '/') {
			return;
		}
		document.title = state.pageTitle
			? `${state.pageTitle} | ${APP_NAME}`
			: APP_NAME;
	}, [state.pageTitle, location.pathname]);

	return (
		<Routes>
			<Route
				path="/"
				element={
					<Landing toggleDarkMode={toggleDarkMode} darkMode={darkMode} />
				}
			/>
			<Route
				path="/pricing"
				element={
					<PricingPage toggleDarkMode={toggleDarkMode} darkMode={darkMode} />
				}
			/>
			<Route path="/login" element={<Login />} />
			<Route path="/forgot-password" element={<ForgotPassword />} />
			<Route path="/reset-password" element={<ResetPassword />} />
			<Route path="/signup" element={<Signup />} />
			<Route path="/signup/complete" element={<SignupComplete />} />
			<Route path="/terms" element={<Terms />} />
			<Route path="/privacy" element={<Privacy />} />
			<Route path="/sms-opt-in" element={<SmsOptIn />} />
			<Route path="/bid-accepted" element={<BidAccepted />} />
			<Route path="/bid-status" element={<BidStatus />} />
			<Route path="/payment/success" element={<PaymentSuccess />} />
			<Route path="/payment/cancelled" element={<PaymentCancelled />} />
			<Route
				path="/jobs"
				element={
					<ProtectedRoute>
						<ManagerRoute>
							<JobDetailProvider>
								<Layout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
									<JobListPage />
								</Layout>
							</JobDetailProvider>
						</ManagerRoute>
					</ProtectedRoute>
				}
			/>
			<Route
				path="/jobs/view/:id"
				element={
					<ProtectedRoute>
						<ManagerRoute>
							<JobDetailProvider>
								<Layout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
									<JobDetailPage />
								</Layout>
							</JobDetailProvider>
						</ManagerRoute>
					</ProtectedRoute>
				}
			/>
			<Route
				path="/schedule"
				element={
					<ProtectedRoute>
						<Layout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
							<SchedulePage />
						</Layout>
					</ProtectedRoute>
				}
			/>
			<Route
				path="/time"
				element={
					<ProtectedRoute>
						<Layout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
							<TimePage />
						</Layout>
					</ProtectedRoute>
				}
			/>
			<Route
				path="/analytics"
				element={
					<ProtectedRoute>
						<ManagerRoute>
							<Layout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
								<AnalyticsPage />
							</Layout>
						</ManagerRoute>
					</ProtectedRoute>
				}
			/>
			<Route
				path="/customers"
				element={
					<ProtectedRoute>
						<ManagerRoute>
							<Layout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
								<Customers />
							</Layout>
						</ManagerRoute>
					</ProtectedRoute>
				}
			/>
			<Route
				path="/users"
				element={
					<ProtectedRoute>
						<ManagerRoute>
							<UsersRoute>
								<Layout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
									<Users />
								</Layout>
							</UsersRoute>
						</ManagerRoute>
					</ProtectedRoute>
				}
			/>
			<Route
				path="/settings"
				element={
					<ProtectedRoute>
						<ManagerRoute>
							<Layout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
								<Settings />
							</Layout>
						</ManagerRoute>
					</ProtectedRoute>
				}
			/>
			<Route
				path="/organizations"
				element={
					<ProtectedRoute>
						<SuperuserRoute>
							<Layout toggleDarkMode={toggleDarkMode} darkMode={darkMode}>
								<Organizations />
							</Layout>
						</SuperuserRoute>
					</ProtectedRoute>
				}
			/>
			<Route path="*" element={<NotFound />} />
		</Routes>
	);
}

function App() {
	const [darkMode, setDarkMode] = useState(() => {
		const saved = localStorage.getItem('darkMode');
		return saved !== null ? JSON.parse(saved) : false;
	});

	useEffect(() => {
		localStorage.setItem('darkMode', JSON.stringify(darkMode));
	}, [darkMode]);

	const theme = useMemo(
		() => getTheme(darkMode ? 'dark' : 'light'),
		[darkMode],
	);

	const toggleDarkMode = () => {
		setDarkMode(!darkMode);
	};

	return (
		<ThemeProvider theme={theme}>
			<SnackbarProvider>
				<CssBaseline />
				<PageTransitionLoader />
				<AppProvider>
					<RealtimeProvider>
						<AppContent toggleDarkMode={toggleDarkMode} darkMode={darkMode} />
					</RealtimeProvider>
				</AppProvider>
			</SnackbarProvider>
		</ThemeProvider>
	);
}

export default App;
